import { Component, EventEmitter, Input, OnInit, Output, ViewEncapsulation, inject } from '@angular/core';
import { Action } from './enums/action';
import { IAction } from './interface/iaction';
import { AuthService } from '@core/services/auth.service';
import { Role } from '@core/enums/roles';
import { IColumnSort } from '@core/interfaces/itable';
import { Router } from '@angular/router';

@Component({
  selector: 'prism-table',
  templateUrl: './table.component.html',
  styleUrl: './table.component.scss',
  encapsulation: ViewEncapsulation.None
})
export class TableComponent implements OnInit {
  private authService = inject(AuthService);
  private router = inject(Router);
  @Output() pageIndexChange = new EventEmitter<number>();
  @Output() pageSizeChange = new EventEmitter<number>();
  @Output() columnDirectionChange = new EventEmitter<IColumnSort>();
  @Output() add = new EventEmitter();
  @Output() edit = new EventEmitter();
  @Output() delete = new EventEmitter();
  @Output() navigate = new EventEmitter();
  @Output() passwordReset = new EventEmitter();
  @Input() columns: string[] = [];
  @Input() colHeaders: any;
  @Input() tableData: any[] = []
  @Input() isDelete = false;
  @Input() isEdit = false;
  @Input() pageSize = 10;
  @Input() pageIndex = 1;
  @Input() totalData = 0;
  @Input() hideActionBtns: boolean = false;
  @Input() columnTypes: Record<string, 'string' | 'number' | 'date'> = {};

  actions: IAction[] = [];
  actionType = Action;
  currentUser = this.authService.getCurrentUser();
  isDistrictAdmin = this.currentUser.roleId === Role.DistrictAdmin;
  isMobile = false;
  sortColumn: string | null = null;
  sortDirection: 'asc' | 'desc' = 'asc';
  url = this.router.url;

  ngOnInit(): void {
    this.actions = [
      {
        label: 'Edit',
        isActive: this.isEdit,
        icon: 'uil uil-edit text-dark-purple',
        handler: this.action.bind(this),
        type: this.actionType.edit
      },
       {
        label: 'Edit',
        isActive: this.url.includes('user-management') ? this.isEdit : false,
        icon: 'uil uil-lock text-dark-purple',
        handler: this.action.bind(this),
        type: this.actionType.passwordReset
      },
      {
        label: 'Delete',
        isActive: this.isEdit,
        icon: 'uil uil-times-circle text-red',
        handler: this.action.bind(this),
        type: this.actionType.delete
      },
    ];
    if (this.hideActionBtns) {
      this.actions = this.actions.filter(action =>
        action.type !== this.actionType.delete && action.type !== this.actionType.edit
      );
    }
    if (this.colHeaders && this.colHeaders.hasOwnProperty('schoolCount')) {
      this.actions.unshift({
        label: 'Schools',
        isActive: this.isEdit,
        icon: 'uil uil-clipboard-alt text-dark-purple',
        handler: this.action.bind(this),
        type: this.actionType.navigate
      });
    }
  }

  action(item: any, actionType: string) {
    switch (actionType) {
      case Action.edit:
        this.edit.emit(item);
        break;
      case Action.delete:
        this.delete.emit(item);
        break;
      case Action.add:
        this.add.emit(item);
        break;
      case Action.navigate:
        this.navigate.emit(item);
        break;
      case Action.passwordReset:
        this.passwordReset.emit(item);
        break;
      default:
        break;
    }
  }

  handlePageIndexChange(pageIndex: number): void {
    this.pageIndex = pageIndex;
    this.pageIndexChange.emit(pageIndex);
  }

  handlePageSizeChange(pageSize: number): void {
    this.pageSize = pageSize;
    this.pageSizeChange.emit(pageSize);
  }

  handleSortByColumn(column: string): void {
    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDirection = 'asc';
    }

    this.columnDirectionChange.emit({
      column: this.sortColumn,
      direction: this.sortDirection
    });
  }

  sortByColumn(column: string) {
  const isSameColumn = this.sortColumn === column;
  this.sortDirection = isSameColumn
    ? (this.sortDirection === 'asc' ? 'desc' : 'asc')
    : 'asc';
  this.sortColumn = column;

  const sortedData = [...this.tableData].sort((a, b) => {
    const valA = a[column];
    const valB = b[column];

    if (valA == null) return this.sortDirection === 'asc' ? 1 : -1;
    if (valB == null) return this.sortDirection === 'asc' ? -1 : 1;

    const columnType = this.columnTypes[column] || 'string';
    let comparison = 0;

    switch (columnType) {
      case 'number':
        comparison = parseFloat(valA) - parseFloat(valB);
        break;
      case 'date':
        comparison = new Date(valA).getTime() - new Date(valB).getTime();
        break;
      case 'string':
      default:
        comparison = valA.toString().trim().toLowerCase()
          .localeCompare(valB.toString().trim().toLowerCase());
        break;
    }

    return this.sortDirection === 'asc' ? comparison : -comparison;
  });

  this.tableData = sortedData;
}

}
